/*
 * DialUpDuo dark mode: header toggle behavior.
 *
 * The inline boot script in layout/theme.liquid resolves the initial theme
 * before first paint (stored choice beats OS preference); this file owns
 * everything after that: toggle clicks, persistence, live OS-preference
 * follow, aria-pressed, the meta theme-color, and the duo:theme:change
 * event other scripts (blob canvas, arcade) listen to.
 *
 * Storage contract: localStorage['duo:theme:v1'] holds 'light' or 'dark'
 * only after an explicit click. Guest-first, no identifiers. When storage
 * is blocked (Safari private mode) an in-memory choice keeps the session
 * working, same guard pattern as duo-wishlist.js.
 */
(() => {
  'use strict';

  const KEY = 'duo:theme:v1';
  const META_COLORS = { light: '#fef9f3', dark: '#211a2f' };
  const root = document.documentElement;
  const darkMedia = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  const reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  let memoryChoice = null; // fallback when storage is blocked

  function readChoice() {
    let value = memoryChoice;
    try {
      value = localStorage.getItem(KEY) || memoryChoice;
    } catch (error) {
      /* storage blocked: memoryChoice carries the session */
    }
    return value === 'light' || value === 'dark' ? value : null;
  }

  function writeChoice(theme) {
    memoryChoice = theme;
    try {
      localStorage.setItem(KEY, theme);
    } catch (error) {
      /* storage blocked: session-only theming still works */
    }
  }

  function currentTheme() {
    return root.dataset.theme === 'dark' ? 'dark' : 'light';
  }

  function apply(theme) {
    root.dataset.theme = theme;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', META_COLORS[theme]);
    document.querySelectorAll('[data-duo-theme-toggle]').forEach((button) => {
      button.setAttribute('aria-pressed', theme === 'dark' ? 'true' : 'false');
    });
    document.dispatchEvent(new CustomEvent('duo:theme:change', { detail: { theme } }));
  }

  function applyWithTransition(theme) {
    const instant = reducedMotion && reducedMotion.matches;
    if (!instant && typeof document.startViewTransition === 'function') {
      document.startViewTransition(() => apply(theme));
    } else {
      apply(theme);
    }
  }

  function onToggleClick() {
    const next = currentTheme() === 'dark' ? 'light' : 'dark';
    writeChoice(next);
    applyWithTransition(next);
    syncControls();
  }

  function onSystemChange(event) {
    if (readChoice()) return; // an explicit choice beats the OS forever after
    applyWithTransition(event.matches ? 'dark' : 'light');
  }

  /*
   * Appearance choice: 'light' | 'dark' | 'auto'.
   *
   * 'auto' is not a stored value. It clears the key, which restores the
   * original contract (no stored choice means the OS decides) and keeps the
   * boot script in layout/theme.liquid unchanged. Before this existed the
   * first toggle click stranded a visitor on a manual choice forever with no
   * way back to following their device; the quick-settings panel exposes it.
   */
  function systemTheme() {
    return darkMedia && darkMedia.matches ? 'dark' : 'light';
  }

  function currentChoice() {
    return readChoice() || 'auto';
  }

  function setChoice(choice) {
    if (choice === 'auto') {
      memoryChoice = null;
      try {
        localStorage.removeItem(KEY);
      } catch (error) {
        /* storage blocked: memoryChoice already cleared for the session */
      }
      applyWithTransition(systemTheme());
    } else if (choice === 'light' || choice === 'dark') {
      writeChoice(choice);
      applyWithTransition(choice);
    }
    syncControls();
  }

  function syncControls() {
    const choice = currentChoice();
    document.querySelectorAll('[data-duo-theme-choice]').forEach((button) => {
      const isCurrent = button.dataset.duoThemeChoice === choice;
      button.setAttribute('aria-checked', isCurrent ? 'true' : 'false');
      button.tabIndex = isCurrent ? 0 : -1;
    });
  }

  window.duoTheme = {
    get choice() {
      return currentChoice();
    },
    get resolved() {
      return currentTheme();
    },
    set: setChoice,
    sync: syncControls,
  };

  function init() {
    document.querySelectorAll('[data-duo-theme-toggle]').forEach((button) => {
      button.addEventListener('click', onToggleClick);
      button.setAttribute('aria-pressed', currentTheme() === 'dark' ? 'true' : 'false');
    });
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-duo-theme-choice]');
      if (button) setChoice(button.dataset.duoThemeChoice);
    });
    syncControls();
    if (darkMedia) {
      if (typeof darkMedia.addEventListener === 'function') darkMedia.addEventListener('change', onSystemChange);
      else if (typeof darkMedia.addListener === 'function') darkMedia.addListener(onSystemChange);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
